import { z } from 'zod';

import { approvalStatusEnum } from '../incoming-items/types';

/**
 * Destination Company DTO — mirrors `03-database-spec.md` §4.10.
 *
 * `japanese_name` is denormalized off the linked `companies` row for the
 * list/table display. Frontend treats it as nullable in case the backend
 * does not join it for every list shape.
 */

export const destinationTypeEnum = z.enum(['buyer', 'secondary_processor']);

export const destinationSchema = z.object({
  destination_company_id: z.string(),
  company_id: z.string(),
  /** Joined from `companies.display_name`. Always present on list/create. */
  company_display_name: z.string(),
  /** Joined from `companies.legal_name`. */
  company_legal_name: z.string(),
  /** Legacy alias kept for older code paths; use `company_display_name`. */
  japanese_name: z.string().nullable().optional(),
  destination_type: destinationTypeEnum,
  /** Deprecated read-only compatibility fields; never write these from new UI. */
  resource_processor_flag: z.boolean().optional(),
  buyer_flag: z.boolean().optional(),
  approval_status: approvalStatusEnum,
  approved_by: z.string().nullable().optional(),
  approved_at: z.string().nullable().optional(),
  certificate_expires_at: z.string().nullable().optional(),
});

export type Destination = z.infer<typeof destinationSchema>;

/**
 * Destinations register by name only — fully decoupled from the party and
 * valuation companies (2026-07-30). The backend resolves the name inside the
 * destination's own company_role namespace and never binds a vendor company.
 */
export const destinationCreateSchema = z.object({
  display_name: z.string(),
  legal_name: z.string().optional(),
  destination_type: destinationTypeEnum,
  certificate_expires_at: z.string().optional(),
});

export type DestinationCreateInput = z.infer<typeof destinationCreateSchema>;
